import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useState } from "react";

export type WorkspaceAddMode = "reference" | "copy";

/**
 * The Add from workspace fork for same-workspace adds (picker picks and rail
 * drags alike): a live reference is the routine primary (A2), an
 * independent copy the labeled secondary. Cross-workspace adds go through
 * CrossWorkspaceFileDropDialog instead — same fork, with the access caveat
 * and copy as the default.
 */
export function WorkspaceAddModeDialog({
  count,
  fileName,
  destination,
  onChoose,
  onClose,
}: {
  count: number;
  /** Shown when count === 1. */
  fileName?: string;
  destination: "canvas" | "chat";
  onChoose: (
    choice: WorkspaceAddMode,
    opts: { alwaysReference: boolean },
  ) => void;
  onClose: () => void;
}) {
  const [alwaysReference, setAlwaysReference] = useState(false);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md" data-testid="workspace-add-mode">
        <DialogHeader>
          {/* A filename is usually one long unbroken token, which has no break
              opportunity and ran straight out of the dialog. `overflow-wrap`
              gives it one; `line-clamp` stops a pathological name from turning
              the header into a paragraph; `leading-snug` because the base
              title is `leading-none`, which collides once it wraps. Done in
              CSS so the title stays a single text node. */}
          <DialogTitle className="line-clamp-3 leading-snug [overflow-wrap:anywhere]">
            {count === 1
              ? `Add "${fileName ?? "this file"}"?`
              : `Add ${count} files?`}
          </DialogTitle>
          <DialogDescription>
            A live reference stays linked to the workspace — updates show up on
            this {destination} too. A copy is independent: changes here never
            touch the original.
          </DialogDescription>
        </DialogHeader>
        {/* The remembered answer: skips this dialog from the next pick on,
            always adding live references. Reset lives in Settings; the
            cross-workspace dialog is exempt and keeps asking. */}
        <label className="flex cursor-pointer select-none items-center gap-2 text-sm text-muted-foreground">
          <Checkbox
            checked={alwaysReference}
            onCheckedChange={(v) => setAlwaysReference(v === true)}
          />
          Always add as a live reference — don&apos;t ask again
        </label>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={() => onChoose("copy", { alwaysReference })}
          >
            {count === 1 ? "Add a copy" : "Add copies"}
          </Button>
          <Button onClick={() => onChoose("reference", { alwaysReference })}>
            Add live reference
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
